// @flow
import type { EventTemplate } from "redux/api-types/EventTemplate";

import { parseTemplateNamePrice } from "./templateForm";

export type ContentPricing = {
  costOption: string,
  price: number | string,
  templateId: string,
  error: string,
};

export const getPriceError = (price: ?(number | string)): ?string => {
  const normalized = String(price ?? "").trim();

  if (!normalized) {
    return "Required";
  }

  if (
    !/^\d+(?:\.\d{1,2})?$/.test(normalized) ||
    !Number.isSafeInteger(Math.round(Number(normalized) * 100))
  ) {
    return "Must be a nonnegative amount with up to two decimals";
  }

  return null;
};

export const getApprovedContentPricing = (
  costOptions: ?Array<string>,
  templates: Array<EventTemplate> = []
): ContentPricing => {
  const unavailable = (error: string): ContentPricing => ({
    costOption: "",
    price: "",
    templateId: "",
    error: `${error} Contact your coordinator.`,
  });

  if (!Array.isArray(costOptions) || !costOptions.length) {
    return unavailable("Pricing has not been configured for this class.");
  }

  if (costOptions.length !== 1) {
    return unavailable("This class must have one configured price.");
  }

  const [option] = costOptions;
  if (typeof option !== "string") {
    return unavailable("The configured price for this class is invalid.");
  }

  const costOption = option.trim();
  const amount = /^free$/i.test(costOption) ? "0" : costOption;

  if (getPriceError(amount)) {
    return unavailable("The configured price for this class is invalid.");
  }

  const price = Number(amount);
  const matches = templates.filter(
    (template: EventTemplate): boolean =>
      !!template.id &&
      parseTemplateNamePrice(template.attributes.name) === price
  );

  if (matches.length !== 1) {
    return unavailable(
      matches.length
        ? "More than one pricing template matches this class."
        : "No pricing template matches this class."
    );
  }

  return { costOption, price, templateId: matches[0].id, error: "" };
};
